import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { MAXI_MODIFICATION_MULTIPLIER } from './menuData';

const roundMaxiAdditionPrice = (price) => {
  const dec = price % 1;
  if (dec >= 0.25 && dec < 0.75) return Math.floor(price);
  if (dec >= 0.75) return Math.ceil(price);
  return price;
};

/**
 * useOutsideClose — chiude il dropdown quando si clicca/tappa fuori.
 * Usa 'mousedown' per desktop e 'touchend' per mobile, senza preventDefault,
 * così lo scroll della lista non viene bloccato su Android/Chrome.
 */
const useOutsideClose = (ref, onClose) => {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchend', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchend', handler);
    };
  }, [ref, onClose]);
};

/**
 * IngredientDropdown
 *
 * Soluzione cross-platform per iOS e Android:
 * - iOS Safari: onBlur scatta prima del click → usiamo onMouseDown/onTouchEnd
 *   con flag per bloccare il blur solo quando necessario
 * - Android Chrome: onPointerDown con preventDefault bloccava lo scroll →
 *   distinguiamo tap (< 10px spostamento) da scroll (>= 10px)
 */
const IngredientDropdown = ({ placeholder, searchTerm, setSearchTerm, items, onSelect }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  // Usato per evitare che onBlur chiuda il dropdown prima che onMouseDown
  // registri la selezione (problema Safari iOS)
  const mouseDownInsideRef = useRef(false);
  // Usato per distinguere tap da scroll su touch device
  const touchStartYRef = useRef(null);

  useOutsideClose(containerRef, () => setOpen(false));

  const filtered = items.filter((item) =>
    (typeof item === 'string' ? item : item.name)
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handleSelect = useCallback((item) => {
    onSelect(item);
    setSearchTerm('');
    setOpen(false);
  }, [onSelect, setSearchTerm]);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm
                   focus:outline-none focus:ring-red-500 focus:border-red-500
                   text-base" /* text-base (16px) evita zoom automatico su iOS */
        placeholder={placeholder}
        value={searchTerm}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        onChange={(e) => { setSearchTerm(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Su iOS onBlur arriva prima di onMouseDown del bottone:
          // se mouseDownInsideRef è true stiamo cliccando dentro → non chiudere
          if (!mouseDownInsideRef.current) {
            setOpen(false);
          }
          mouseDownInsideRef.current = false;
        }}
      />

      {open && filtered.length > 0 && (
        <ul
          className="absolute z-20 w-full bg-white border border-gray-300 rounded-md shadow-lg
                     max-h-52 overflow-y-auto mt-1 list-none p-0 m-0"
          // Impedisce che il click sulla lista triggeri onBlur dell'input (iOS fix)
          onMouseDown={() => { mouseDownInsideRef.current = true; }}
        >
          {filtered.map((item) => {
            const name = typeof item === 'string' ? item : item.name;
            const price = typeof item === 'string' ? null : item.displayPrice;
            return (
              <li key={name}>
                <button
                  type="button"
                  className="w-full text-left px-4 py-3 text-gray-800 hover:bg-gray-100
                             active:bg-gray-200 text-sm leading-snug select-none"
                  // Desktop: onClick normale
                  onClick={() => handleSelect(item)}
                  // Touch: registra punto di inizio per distinguere tap da scroll
                  onTouchStart={(e) => {
                    touchStartYRef.current = e.touches[0].clientY;
                  }}
                  // Touch: seleziona solo se il dito non si è spostato (è un tap, non uno scroll)
                  onTouchEnd={(e) => {
                    if (touchStartYRef.current === null) return;
                    const dy = Math.abs(e.changedTouches[0].clientY - touchStartYRef.current);
                    touchStartYRef.current = null;
                    if (dy < 10) {
                      // È un tap → seleziona l'ingrediente
                      e.preventDefault(); // evita il doppio-trigger con onClick
                      handleSelect(item);
                    }
                    // Se dy >= 10 è uno scroll → non fare nulla, lascia scorrere la lista
                  }}
                >
                  {name}{price != null ? ` (€${price.toFixed(2)})` : ''}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

IngredientDropdown.propTypes = {
  placeholder: PropTypes.string.isRequired,
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ name: PropTypes.string.isRequired, displayPrice: PropTypes.number }),
    ])
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
};

// ─────────────────────────────────────────────
//  CustomizationSection
// ─────────────────────────────────────────────
const CustomizationSection = ({
  itemBase,
  currentAdditions,
  currentRemovals,
  onAddAddition,
  onRemoveAddition,
  onAddRemoval,
  onRemoveRemoval,
  availableAddableExtras,
  removableIngredients,
}) => {
  const isMaxi = itemBase && itemBase.isMaxi;

  const getDisplayPrice = (extra) =>
    isMaxi
      ? roundMaxiAdditionPrice(extra.price * MAXI_MODIFICATION_MULTIPLIER)
      : extra.price;

  const additionItems = availableAddableExtras
    .filter(
      (extra) =>
        !currentAdditions.some((m) => m.name.toLowerCase() === extra.name.toLowerCase()) &&
        !(isMaxi && extra.name.toLowerCase().includes('impasto'))
    )
    .map((extra) => ({ ...extra, displayPrice: getDisplayPrice(extra) }));

  const removalItems = removableIngredients.filter(
    (ing) => !currentRemovals.some((r) => r.toLowerCase() === ing.toLowerCase())
  );

  const [addSearch, setAddSearch] = useState('');
  const [removeSearch, setRemoveSearch] = useState('');

  const handleAddExtra = (extra) => {
    onAddAddition(extra.name, getDisplayPrice(extra));
    setAddSearch('');
  };

  const handleAddRemoval = (name) => {
    onAddRemoval(name);
    setRemoveSearch('');
  };

  return (
    <>
      {/* Aggiungi Ingredienti */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-900">Aggiungi Ingredienti Extra:</h4>
        <IngredientDropdown
          placeholder="Cerca o digita un ingrediente…"
          searchTerm={addSearch}
          setSearchTerm={setAddSearch}
          items={additionItems}
          onSelect={handleAddExtra}
        />
        {currentAdditions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {currentAdditions.map((mod, index) => (
              <span
                key={index}
                className="inline-flex items-center bg-green-100 text-green-800
                           text-xs font-medium px-2.5 py-1 rounded-full"
              >
                +{mod.name} (€{mod.price.toFixed(2)})
                <button
                  type="button"
                  onClick={() => onRemoveAddition(index)}
                  className="ml-1.5 text-green-600 hover:text-green-900 text-base leading-none"
                  aria-label={`Rimuovi ${mod.name}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Rimuovi Ingredienti */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-900">Rimuovi Ingredienti:</h4>
        <IngredientDropdown
          placeholder="Cerca o digita un ingrediente da rimuovere…"
          searchTerm={removeSearch}
          setSearchTerm={setRemoveSearch}
          items={removalItems}
          onSelect={handleAddRemoval}
        />
        {currentRemovals.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {currentRemovals.map((mod, index) => (
              <span
                key={index}
                className="inline-flex items-center bg-red-100 text-red-800
                           text-xs font-medium px-2.5 py-1 rounded-full"
              >
                -{mod}
                <button
                  type="button"
                  onClick={() => onRemoveRemoval(index)}
                  className="ml-1.5 text-red-600 hover:text-red-900 text-base leading-none"
                  aria-label={`Annulla rimozione ${mod}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

CustomizationSection.propTypes = {
  itemBase: PropTypes.object,
  currentAdditions: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string.isRequired, price: PropTypes.number.isRequired })
  ).isRequired,
  currentRemovals: PropTypes.arrayOf(PropTypes.string).isRequired,
  onAddAddition: PropTypes.func.isRequired,
  onRemoveAddition: PropTypes.func.isRequired,
  onAddRemoval: PropTypes.func.isRequired,
  onRemoveRemoval: PropTypes.func.isRequired,
  availableAddableExtras: PropTypes.array.isRequired,
  removableIngredients: PropTypes.array.isRequired,
};

export default CustomizationSection;
